import { useTranslation } from 'module/hooks/use-translation';
import { ReactComponent as AiLoaderIcon } from 'module/ui/assets/svgs/ai-loader.svg';
import { Input } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { StreamAPI, DocumentAgentStreamData } from 'api/stream';
import { useAccountInitialDataQuery } from 'queries/account';
import { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-large);
  padding: 16px;
  width: 652px;
  box-shadow: var(--shadow-08);
`;

const TitleInput = styled(Input)`
  font-size: 28px;
  line-height: 1.3;
  border: none;
  padding: 0 !important;
  width: 100%;

  input {
    font-size: 28px;
    border: none;
    padding: 0 !important;

    &:focus {
      outline: none;
      border: none;
    }

    &:disabled {
      color: #000;
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;
  justify-content: space-between;
  width: 100%;
`;

const ProgressTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  align-items: center;
  overflow-y: auto;
`;

const Container = styled.div`
  padding: 48px 64px;
  height: 100%;
  background: #fff;
  min-width: 768px;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: #000;
  transition: width 0.3s ease;
`;

interface DocumentAgentGenerationScreenProps {
  documentId: number;
  documentTitle: string;
  onComplete: () => void;
  updating: boolean;
}

export const DocumentAgentGenerationScreen = ({
  documentId,
  documentTitle,
  onComplete,
  updating,
}: DocumentAgentGenerationScreenProps) => {
  const updatingLabel = useTranslation('document_agent_generation.updating');
  const generatingLabel = useTranslation(
    'document_agent_generation.generating',
  );
  const [progress, setProgress] = useState(0);
  const [progressTitle, setProgressTitle] = useState(
    updating ? updatingLabel : generatingLabel,
  );
  const queryClient = useQueryClient();
  const { data: accountInitialData } = useAccountInitialDataQuery();

  const handleProgressUpdate = useCallback((data: DocumentAgentStreamData) => {
    if (data.data.description) {
      setProgressTitle(data.data.description);
    }

    if (data.data.progress !== undefined) {
      setProgress(data.data.progress);
    }
  }, []);

  const handleStreamingComplete = useCallback(async () => {
    // Invalidate and refetch the document
    await queryClient.invalidateQueries({
      queryKey: ['document', documentId],
    });

    // Invalidate the current company's pages list so the new/updated document appears
    await queryClient.invalidateQueries({
      queryKey: ['pages', 'multiple'],
    });

    // Call onComplete after a short delay to allow state updates
    setTimeout(() => {
      onComplete();
    }, 500);
  }, [documentId, onComplete, queryClient]);

  const handleProgressUpdateRef = useRef(handleProgressUpdate);

  handleProgressUpdateRef.current = handleProgressUpdate;

  const handleStreamingCompleteRef = useRef(handleStreamingComplete);

  handleStreamingCompleteRef.current = handleStreamingComplete;

  useEffect(() => {
    const accountId = accountInitialData?.account.accountId;

    if (!accountId) {
      return;
    }

    let abortController: AbortController | null = null;
    let cancelled = false;

    const startStreaming = async () => {
      try {
        const authResponse =
          await StreamAPI.getDocumentAgentStreamAuthToken(documentId);

        if (cancelled) {
          return;
        }

        const channelName = `accountId_${accountId}_documentId_${documentId}`;

        abortController = new AbortController();

        await StreamAPI.streamDocumentAgentProgress({
          token: authResponse.token,
          channelName,
          onProgressUpdate: (data) => handleProgressUpdateRef.current(data),
          onStreamingComplete: () => {
            void handleStreamingCompleteRef.current();
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Streaming error:', error);
      }
    };

    void startStreaming();

    return () => {
      cancelled = true;

      if (abortController) {
        abortController.abort();
      }
    };
  }, [documentId, accountInitialData?.account.accountId]);

  return (
    <Wrapper>
      <Container>
        <TitleContainer>
          <TitleInput value={documentTitle} disabled={true} />
        </TitleContainer>
        <ProgressContainer>
          <ProgressTitle>
            <AiLoaderIcon /> <span>{progressTitle}</span>
          </ProgressTitle>
          <ProgressBarContainer>
            <ProgressBarFill progress={progress} />
          </ProgressBarContainer>
        </ProgressContainer>
      </Container>
    </Wrapper>
  );
};
